import { Button } from '@/components/atoms/Button';
import { Checkbox } from '@/components/atoms/Checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/atoms/Popover';
import { TableHead, TableHeader, TableRow } from '@/components/atoms/Table';
import Image from 'next/image';
import { useState } from 'react';
import { OrderingField, OrderingState } from '../../types/api/product';
import { Filter } from '../../types/models/filter';

interface ProductTableHeaderProps {
  filters: Filter[];
  tempFilters: Filter[];
  ordering: OrderingState;
  toggleOrdering: (field: OrderingField) => void;
  applyTempFilters: () => void;
  resetTempFilter: (filterId: string) => void;
  handleChangeCheckedTempFilter: (filterId: string, optionName: string) => void;
  restoreOriginalFilters: () => void;
  isAuthenticated: boolean;
}

export function ProductTableHeader({
  filters,
  tempFilters,
  ordering,
  toggleOrdering,
  applyTempFilters,
  resetTempFilter,
  handleChangeCheckedTempFilter,
  restoreOriginalFilters,
  isAuthenticated,
}: ProductTableHeaderProps) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>(
    {},
  );

  // 컬럼 설정 - 고정 너비 추가
  const columns = [
    {
      id: 'cart',
      label: '장바구니',
      sortable: false,
      filterable: false,
      width: 'w-18',
    },
    {
      id: 'meat_type',
      label: '축종',
      sortable: false,
      filterable: true,
      width: 'w-15',
    },
    {
      id: 'origin',
      label: '원산지',
      sortable: false,
      filterable: true,
      width: 'w-20',
    },
    {
      id: 'storage_type',
      label: '보관',
      sortable: false,
      filterable: true,
      width: 'w-16',
    },
    {
      id: 'meat_loin',
      label: '품목',
      sortable: false,
      filterable: true,
      width: 'w-30',
    },
    {
      id: 'brand',
      label: '브랜드',
      sortable: false,
      filterable: true,
      width: 'w-48',
    },
    {
      id: 'grade',
      label: '등급',
      sortable: false,
      filterable: true,
      width: 'w-22',
    },
    {
      id: 'EST',
      label: 'EST',
      sortable: false,
      filterable: true,
      width: 'w-20',
    },
    {
      id: 'average_weight',
      label: '평중(kg)',
      sortable: true,
      filterable: false,
      field: 'average_weight',
      width: 'w-20',
    },
    {
      id: 'description',
      label: '스펙/설명',
      sortable: false,
      filterable: false,
      width: 'w-[142px]',
    },
    {
      id: 'stock',
      label: '재고(box)',
      sortable: true,
      filterable: false,
      field: 'quantity',
      width: 'w-22',
    },
    {
      id: 'warehouse',
      label: '창고',
      sortable: false,
      filterable: true,
      width: 'w-25',
    },
    {
      id: 'expiration_date',
      label: '소비기한',
      sortable: true,
      filterable: false,
      field: 'expiration_date',
      width: 'w-22',
    },
    {
      id: 'unit_price',
      label: '판매가(원)',
      sortable: isAuthenticated,
      filterable: false,
      field: 'unit_price',
      width: 'w-26',
    },
    {
      id: 'days_since_update',
      label: '수정일',
      sortable: true,
      filterable: false,
      field: 'days_since_update',
      width: 'w-18',
    },
    {
      id: 'company',
      label: '판매처',
      sortable: false,
      filterable: isAuthenticated,
      width: 'w-[136px]',
    },
    {
      id: 'message',
      label: '메세지',
      enabled: isAuthenticated,
      sortable: false,
      filterable: false,
      width: 'w-16',
    },
  ];

  // 정렬 아이콘 렌더링
  const renderSortIcon = (field: OrderingField) => {
    if (ordering.field !== field)
      return (
        <Image
          src="/images/arrow-up-down.svg"
          alt="arrowupdown"
          width={20}
          height={20}
        />
      );
    return ordering.direction === 'asc' ? (
      <Image src="/images/arrow-up.svg" alt="arrowup" width={20} height={20} />
    ) : (
      <Image
        src="/images/arrow-down.svg"
        alt="arrowdown"
        width={20}
        height={20}
      />
    );
  };

  // 필터 연결
  const getFilterForColumn = (columnId: string) => {
    // columnId와 filter.id 매핑 (필요한 경우 맞춤 설정)
    return filters.find((f) => f.id === columnId);
  };

  // 필터가 적용되었는지 확인하는 함수
  const isFilterApplied = (columnId: string) => {
    const filter = filters.find((f) => f.id === columnId);
    if (!filter) return false;

    return filter.options.some((option) => option.selected);
  };

  // 필터 검색
  const handleSearchChange = (filterId: string, value: string) => {
    setSearchQueries((prev) => ({
      ...prev,
      [filterId]: value,
    }));
  };

  return (
    <TableHeader>
      <TableRow>
        {columns.map((column) => {
          if (column.enabled === false) return null;

          const filter = column.filterable
            ? getFilterForColumn(column.id)
            : null;

          // 필터나 정렬이 적용되었는지 확인
          const isActive =
            (column.sortable && ordering.field === column.field) ||
            (column.filterable && isFilterApplied(column.id));

          return (
            <TableHead
              key={column.id}
              className={`${column.width} ${filter || column.sortable ? 'pr-1' : ''}`}
            >
              {column.sortable ? (
                <Button
                  variant="ghost"
                  className={`body-14sb gap-0.5 w-full flex justify-center items-center`}
                  onClick={() => toggleOrdering(column.field as OrderingField)}
                >
                  {column.label}
                  {renderSortIcon(column.field as OrderingField)}
                </Button>
              ) : filter ? (
                <Popover
                  open={open[column.id]}
                  onOpenChange={(isOpen) => {
                    if (!isOpen) {
                      // Filter closed without applying
                      setTimeout(() => {
                        restoreOriginalFilters();
                      }, 100);
                    }
                    setOpen({ ...open, [column.id]: isOpen });
                  }}
                >
                  <PopoverTrigger asChild>
                    <div className="flex items-center justify-between w-full cursor-pointer">
                      <span className={isActive ? 'text-(--primary-400)' : ''}>
                        {column.label}
                      </span>
                      <Image
                        src={
                          open[column.id]
                            ? isActive
                              ? '/images/chevron-up-selected.svg'
                              : '/images/chevron-up.svg'
                            : isActive
                              ? '/images/chevron-down-selected.svg'
                              : '/images/chevron-down.svg'
                        }
                        alt="arrow"
                        width={20}
                        height={20}
                      />
                    </div>
                  </PopoverTrigger>
                  <PopoverContent
                    className="p-0 mt-3 w-48 border-(--secondary-200) shadow-lg overflow-hidden"
                    align="start"
                    alignOffset={-11}
                    side="bottom"
                    avoidCollisions
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center border-b">
                        <input
                          className="flex h-10 w-full rounded-md bg-background px-4 text-sm outline-none"
                          placeholder={`${filter.name} 검색`}
                          value={searchQueries[filter.id] || ''}
                          onChange={(e) =>
                            handleSearchChange(filter.id, e.target.value)
                          }
                        />
                      </div>
                      <div className="max-h-[500px] overflow-auto">
                        {filter.options
                          .filter((option) =>
                            option.name
                              .toLowerCase()
                              .includes(
                                (searchQueries[filter.id] || '').toLowerCase(),
                              ),
                          )
                          .map((option) => {
                            const isSelected = tempFilters
                              .find((f) => f.id === filter.id)
                              ?.options.find(
                                (o) => o.name === option.name,
                              )?.selected;
                            return (
                              <div
                                key={option.name}
                                className="flex items-center hover:bg-(--secondary-50) body-14r text-[black] p-2 gap-1"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleChangeCheckedTempFilter(
                                    filter.id,
                                    option.name,
                                  );
                                }}
                              >
                                <Checkbox checked={isSelected} />
                                <label
                                  className="flex-1 cursor-pointer"
                                  onClick={(e) => e.preventDefault()}
                                >
                                  {option.name}
                                </label>
                              </div>
                            );
                          })}
                      </div>
                      <div className="flex items-center justify-between h-10 gap-2 border-t border-t-(--secondary-200) pl-0 pr-2.5 py-2">
                        <Button
                          variant="ghost"
                          size="default"
                          onClick={() => {
                            resetTempFilter(filter.id);
                          }}
                          className="w-fit text-[#4A515D]"
                        >
                          초기화
                        </Button>
                        <Button
                          className="px-2 py-0 h-fit"
                          size="default"
                          onClick={() => {
                            applyTempFilters();
                            setSearchQueries((prev) => ({
                              ...prev,
                              [filter.id]: '',
                            }));
                            setOpen((prev) => ({
                              ...prev,
                              [filter.id]: false,
                            }));
                          }}
                        >
                          적용
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                <span className={isActive ? 'text-(--primary-400)' : ''}>
                  {column.label}
                </span>
              )}
            </TableHead>
          );
        })}
      </TableRow>
    </TableHeader>
  );
}
